export * from "./daily-allowance";

import {
	BACKUP_KDF_ITERATIONS,
	RECOVERY_ALPHABET,
	RECOVERY_CODE_LENGTH,
	RECOVERY_GROUP_SIZE,
	SENSITIVE_CATEGORY_IDS,
	UNLOCK_THRESHOLDS,
} from "./constants";

export * from "./constants";
export * from "./format";
export * from "./household-consumption";
export * from "./kr-holidays";
export * from "./memory-story";
export * from "./monetization";
export * from "./payment-trace";
export * from "./period";
export * from "./restock-forecast";
export * from "./store-rules";

export type PlainDate = `${number}-${number}-${number}`;
export type CategoryStage = 0 | 1 | 2 | 3;

export interface CatalogCategory {
	categoryId: string;
	groupId: string;
	nameKo: string;
	slug: string;
	symbolId: string;
	sensitive: boolean;
	tombstone?: boolean;
	sortOrder: number;
	descKo?: string;
}

export interface CatalogBrand {
	brandId: string;
	nameKo: string;
	categoryId: string;
	colorHex: string;
	aliases: string[];
	sortOrder: number;
}

export interface CatalogKeyword {
	pattern: string;
	categoryId: string;
	priority?: number;
}

export interface Catalog {
	categories: CatalogCategory[];
	brands: CatalogBrand[];
	keywords: CatalogKeyword[];
}

export interface DraftItem {
	name: string;
	amount: number | null;
	qty: number | null;
}

export interface DraftRecord {
	receiptId?: string;
	storeKey: string;
	storeDisplay: string;
	items: DraftItem[];
	memo?: string | null;
	totalAmount: number | null;
	transactedAt: string | null;
	categoryId: string;
	sensitiveMasked?: boolean;
	brandId?: string | null;
	categoryPinned?: boolean;
}

export interface Classification {
	brandId: string | null;
	categoryId: string;
	source: "brand" | "keyword" | "ai" | "fallback";
}

export function normalizeStoreName(raw: string): {
	key: string;
	display: string;
} {
	const display = raw.normalize("NFKC").trim().replace(/\s+/g, " ");
	const lowerWithoutContext = display
		.toLocaleLowerCase("ko-KR")
		.replace(/\([^)]*\)/g, "");
	// `점`은 지점 접미사이기도 하지만 `편의점` 자체는 분류 키다. 단독 업종명을
	// `편의`로 훼손하면 keyword fallback이 깨지므로 이 한 category noun은 보존한다.
	const withoutBranch =
		lowerWithoutContext.trim() === "편의점"
			? lowerWithoutContext
			: lowerWithoutContext.replace(
					/\s*(?:제\d+)?(?:지점|점|store|branch)\s*$/i,
					"",
				);
	const key = withoutBranch.replace(/[\s\-_]/g, "");
	return { key, display };
}

export function classify(input: {
	storeKey: string;
	aiCategoryHint: string | null;
	catalog: Catalog;
}): Classification {
	const exact = input.catalog.brands
		.filter((brand) => brand.aliases.includes(input.storeKey))
		.sort((a, b) => a.brandId.localeCompare(b.brandId))[0];
	if (exact)
		return {
			brandId: exact.brandId,
			categoryId: exact.categoryId,
			source: "brand",
		};

	const prefixes = input.catalog.brands
		.filter((brand) =>
			brand.aliases.some((alias) => input.storeKey.startsWith(alias)),
		)
		.sort((a, b) => {
			const aLength = Math.max(
				...a.aliases
					.filter((alias) => input.storeKey.startsWith(alias))
					.map((alias) => alias.length),
			);
			const bLength = Math.max(
				...b.aliases
					.filter((alias) => input.storeKey.startsWith(alias))
					.map((alias) => alias.length),
			);
			return bLength - aLength || a.brandId.localeCompare(b.brandId);
		})[0];
	if (prefixes)
		return {
			brandId: prefixes.brandId,
			categoryId: prefixes.categoryId,
			source: "brand",
		};

	const keyword = input.catalog.keywords
		.filter((rule) => input.storeKey.includes(rule.pattern))
		.sort(
			(a, b) =>
				b.pattern.length - a.pattern.length ||
				a.categoryId.localeCompare(b.categoryId),
		)[0];
	if (keyword)
		return { brandId: null, categoryId: keyword.categoryId, source: "keyword" };

	const category = input.catalog.categories.find(
		(candidate) => candidate.categoryId === input.aiCategoryHint,
	);
	if (category)
		return { brandId: null, categoryId: category.categoryId, source: "ai" };
	return { brandId: null, categoryId: "etc-other", source: "fallback" };
}

export function unlockStage(visitCount: number): CategoryStage {
	if (visitCount >= UNLOCK_THRESHOLDS[2]) return 3;
	if (visitCount >= UNLOCK_THRESHOLDS[1]) return 2;
	if (visitCount >= UNLOCK_THRESHOLDS[0]) return 1;
	return 0;
}

export function applySensitivityPolicy(
	record: DraftRecord,
	categoryId: string,
	categories: Pick<
		CatalogCategory,
		"categoryId" | "nameKo" | "sensitive"
	>[] = SENSITIVE_CATEGORY_IDS.map((id) => ({
		categoryId: id,
		nameKo:
			id === "health-pharmacy"
				? "약국"
				: id === "health-clinic"
					? "병의원"
					: id === "health-dental"
						? "치과"
						: id === "health-oriental"
							? "한의원"
							: "건강검진",
		sensitive: true,
	})),
): DraftRecord {
	const category = categories.find((item) => item.categoryId === categoryId);
	if (!category?.sensitive)
		return { ...record, categoryId, sensitiveMasked: false };
	return {
		...record,
		categoryId,
		storeDisplay: category.nameKo,
		items: [],
		memo: null,
		sensitiveMasked: true,
	};
}

export interface StreakState {
	lastDate: PlainDate | null;
	current: number;
	longest: number;
	freeze: 0 | 1;
}

function dateToDay(date: PlainDate): number {
	const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
	return Date.UTC(year, month - 1, day) / 86_400_000;
}

export function daysBetween(a: PlainDate, b: PlainDate): number {
	return Math.round(dateToDay(b) - dateToDay(a));
}

export function applyStreak(
	state: StreakState,
	recordDateKst: PlainDate,
): StreakState {
	if (!state.lastDate)
		return {
			...state,
			lastDate: recordDateKst,
			current: 1,
			longest: Math.max(state.longest, 1),
		};
	const gap = daysBetween(state.lastDate, recordDateKst);
	if (gap === 0) return state;
	if (gap === 1) {
		const current = state.current + 1;
		return {
			...state,
			lastDate: recordDateKst,
			current,
			longest: Math.max(state.longest, current),
		};
	}
	if (gap === 2 && state.freeze === 1) {
		const current = state.current + 1;
		return {
			...state,
			lastDate: recordDateKst,
			current,
			freeze: 0,
			longest: Math.max(state.longest, current),
		};
	}
	return {
		...state,
		lastDate: recordDateKst,
		current: 1,
		longest: Math.max(state.longest, 1),
	};
}

export function applyWeeklyFreeze(
	state: StreakState,
	dateKst: PlainDate,
): StreakState {
	const [year = 0, month = 1, day = 1] = dateKst.split("-").map(Number);
	const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
	return weekday === 1 ? { ...state, freeze: 1 } : state;
}

export interface PushWindowPolicy {
	enabled: boolean;
	quietStart: string;
	quietEnd: string;
	lastSentAt: string | null;
}

function minuteOfDay(value: string): number {
	const match = /^(\d{2}):(\d{2})$/.exec(value);
	if (!match) return 0;
	const hour = Number(match[1]);
	const minute = Number(match[2]);
	return Math.min(1439, Math.max(0, hour * 60 + minute));
}

export function isWithinQuietHours(
	now: Date,
	quietStart = "22:00",
	quietEnd = "08:00",
): boolean {
	const current = now.getHours() * 60 + now.getMinutes();
	const start = minuteOfDay(quietStart);
	const end = minuteOfDay(quietEnd);
	if (start === end) return false;
	return start < end
		? current >= start && current < end
		: current >= start || current < end;
}

export function pushDedupKey(kind: "weekly" | "streak", now: Date): string {
	const day = now.toISOString().slice(0, 10);
	return `${kind}:${day}`;
}

export function shouldSendPush(
	policy: PushWindowPolicy,
	kind: "weekly" | "streak",
	now = new Date(),
): boolean {
	if (
		!policy.enabled ||
		isWithinQuietHours(now, policy.quietStart, policy.quietEnd)
	)
		return false;
	if (!policy.lastSentAt) return true;
	return (
		pushDedupKey(kind, now) !== pushDedupKey(kind, new Date(policy.lastSentAt))
	);
}

export function percentile(myCount: number, sortedCounts: number[]): number {
	if (sortedCounts.length === 0) return 50;
	const strictlyLess = sortedCounts.filter((count) => count < myCount).length;
	return Math.min(
		99,
		Math.max(1, Math.floor((strictlyLess / sortedCounts.length) * 100)),
	);
}

export interface ReceiptForDuplicate {
	receiptId: string;
	storeKey: string;
	totalAmount: number | null;
	transactedAt: string | null;
}

export function findDuplicate(
	draft: ReceiptForDuplicate,
	existing: ReceiptForDuplicate[],
): ReceiptForDuplicate | null {
	if (draft.totalAmount === null || draft.transactedAt === null) return null;
	const draftTime = Date.parse(draft.transactedAt);
	if (!Number.isFinite(draftTime)) return null;
	return (
		existing
			.filter(
				(record) =>
					record.storeKey === draft.storeKey &&
					record.totalAmount === draft.totalAmount &&
					record.transactedAt !== null,
			)
			.map((record) => ({
				record,
				distance:
					Math.abs(Date.parse(record.transactedAt as string) - draftTime) /
					1000,
			}))
			.filter(({ distance }) => distance <= 120)
			.sort(
				(a, b) =>
					a.distance - b.distance ||
					a.record.receiptId.localeCompare(b.record.receiptId),
			)[0]?.record ?? null
	);
}

export function completion(
	unlocked: Set<string>,
	catalog: Pick<Catalog, "categories">,
): {
	total: number;
	unlockedCount: number;
	ratio: number;
	byGroup: Record<string, { total: number; unlocked: number }>;
} {
	const categories = catalog.categories;
	const byGroup: Record<string, { total: number; unlocked: number }> = {};
	for (const category of categories) {
		let group = byGroup[category.groupId];
		if (!group) {
			group = { total: 0, unlocked: 0 };
			byGroup[category.groupId] = group;
		}
		group.total += 1;
		if (unlocked.has(category.categoryId)) group.unlocked += 1;
	}
	const unlockedCount = categories.filter((category) =>
		unlocked.has(category.categoryId),
	).length;
	return {
		total: categories.length,
		unlockedCount,
		ratio:
			Math.round((unlockedCount / Math.max(categories.length, 1)) * 10_000) /
			10_000,
		byGroup,
	};
}

export interface CategoryVisitRecord {
	categoryId: string;
	deletedAt?: string | null;
}

/**
 * Rebuilds every category visit count from records. The UI and any sync adapter
 * consume this replay result instead of treating a separately stored dogam cell
 * as product truth.
 */
export function deriveCategoryVisits(
	records: readonly CategoryVisitRecord[],
): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const record of records) {
		if (record.deletedAt) continue;
		counts[record.categoryId] = (counts[record.categoryId] ?? 0) + 1;
	}
	return counts;
}

export interface MonthWindow {
	start: PlainDate;
	end: PlainDate;
	label: string;
}

function formatDate(date: Date): PlainDate {
	return `${date.getUTCFullYear().toString().padStart(4, "0")}-${(date.getUTCMonth() + 1).toString().padStart(2, "0")}-${date.getUTCDate().toString().padStart(2, "0")}` as PlainDate;
}

export function monthWindow(
	date: PlainDate,
	monthStartDay: number,
): MonthWindow {
	if (
		!Number.isInteger(monthStartDay) ||
		monthStartDay < 1 ||
		monthStartDay > 28
	)
		throw new Error("E-VALIDATION-01");
	const [year = 0, month = 1, day = 1] = date.split("-").map(Number);
	const baseMonth = day >= monthStartDay ? month - 1 : month - 2;
	const startDate = new Date(Date.UTC(year, baseMonth, monthStartDay));
	const endDate = new Date(Date.UTC(year, baseMonth + 1, monthStartDay - 1));
	return {
		start: formatDate(startDate),
		end: formatDate(endDate),
		label: formatDate(startDate).slice(0, 7),
	};
}

export function generateRecoveryCode(entropy: Uint8Array): string {
	if (entropy.length < 15)
		throw new Error("recovery entropy must be at least 15 bytes");
	let bitBuffer = 0;
	let bits = 0;
	let index = 0;
	const chars: string[] = [];
	while (chars.length < RECOVERY_CODE_LENGTH) {
		while (bits < 5) {
			bitBuffer = (bitBuffer << 8) | (entropy[index++] ?? 0);
			bits += 8;
		}
		bits -= 5;
		chars.push(RECOVERY_ALPHABET[(bitBuffer >> bits) & 31] as string);
	}
	return chars.reduce(
		(out, char, position) =>
			out +
			(position > 0 && position % RECOVERY_GROUP_SIZE === 0 ? "-" : "") +
			char,
		"",
	);
}

export function normalizeRecoveryCode(code: string): string {
	const normalized = code.replace(/[\s-]/g, "").toUpperCase();
	if (
		normalized.length !== RECOVERY_CODE_LENGTH ||
		[...normalized].some((char) => !RECOVERY_ALPHABET.includes(char))
	)
		throw new Error("E-CODE-01");
	return normalized;
}

export async function deriveBackupKey(
	code: string,
	salt: Uint8Array,
): Promise<CryptoKey> {
	const normalized = normalizeRecoveryCode(code);
	const subtle = globalThis.crypto?.subtle;
	if (!subtle) throw new Error("Web Crypto is unavailable");
	const material = await subtle.importKey(
		"raw",
		new TextEncoder().encode(normalized),
		"PBKDF2",
		false,
		["deriveKey"],
	);
	return subtle.deriveKey(
		{
			name: "PBKDF2",
			salt: salt as unknown as BufferSource,
			iterations: BACKUP_KDF_ITERATIONS,
			hash: "SHA-256",
		},
		material,
		{ name: "AES-GCM", length: 256 },
		false,
		["encrypt", "decrypt"],
	);
}

export interface ReceiptCategoryState {
	categoryId: string;
	categoryPinned: boolean;
}

export function resolveCategory(
	record: ReceiptCategoryState,
	catalog: Pick<Catalog, "categories">,
): { categoryId: string; missing: boolean } {
	const exists = catalog.categories.some(
		(category) => category.categoryId === record.categoryId,
	);
	if (record.categoryPinned)
		return { categoryId: record.categoryId, missing: !exists };
	return exists
		? { categoryId: record.categoryId, missing: false }
		: { categoryId: "etc-other", missing: true };
}

export function selectableCategories<T extends object>(
	categories: readonly T[],
): T[] {
	return categories.filter(
		(category) => (category as { tombstone?: boolean }).tombstone !== true,
	);
}
